import type { CrowdexAssistant } from '@crowdex/agent-contracts/api';
import { useMemo } from 'react';
import { useWorkspaceDirectory, type WorkspaceDirectoryState } from '@/lib/assistants/directory';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';

/**
 * Where the user is when they call (plan §2.12, D28): an assistant (its channel or one of its
 * conversations), or none. Live is the voice of that assistant; outside any, of the home one.
 */
export type LivePlace = { kind: 'project'; project: CrowdexAssistant } | { kind: 'none' };

/** The place of the conversation on screen: its assistant, if it has one. */
export function livePlace(
  directory: Pick<WorkspaceDirectoryState, 'projects' | 'placements'>,
  conversation: string | null,
): LivePlace {
  const placement = conversation === null ? undefined : directory.placements[conversation];
  const project =
    (conversation === null
      ? undefined
      : directory.projects.find(
          (candidate) =>
            candidate.conversationId !== null && candidate.conversationId === conversation,
        )) ??
    (placement?.projectId
      ? directory.projects.find((candidate) => candidate.id === placement.projectId)
      : undefined);
  return project === undefined ? { kind: 'none' } : { kind: 'project', project };
}

/** The assistant a call from a place reaches: the place's; null outside any (the home one). */
export function placeAssistant(place: LivePlace): string | null {
  return place.kind === 'project' ? place.project.id : null;
}

/**
 * The call's chip, under its title: the assistant it reaches or speaks as, in its colour, and its
 * name alone for the picture in picture ("Live · 2:14 · Infra & CI").
 */
export function liveChip(assistant: { title: string; color: string } | null | undefined): {
  label: string;
  name: string;
  color: string | null;
} {
  const name = assistant?.title ?? 'Assistant';
  return { label: name, name, color: assistant?.color ?? null };
}

/** The assistant the call speaks as (or reaches, before one), among the user's. */
export function speakingAssistant<T extends { id: string }>(call: {
  speaker: string | null;
  assistants: readonly T[];
}): T | undefined {
  return call.assistants.find((entry) => entry.id === call.speaker);
}

/** Who hears of the call: nobody but the user. */
export function livePrivacy(): string {
  return 'private';
}

/** What is on screen during the call: the title of the conversation the call starts from. */
export function onScreenLabel(
  conversation: string | null,
  threads: readonly { id: string; title: string }[],
): string | null {
  if (conversation === null) return null;
  const title = threads.find((thread) => thread.id === conversation)?.title.trim();
  return title ? `${title} on screen` : null;
}

/**
 * The place of the conversation `conversation` (the one the call starts from), the assistant the
 * call reaches from there and what is on screen.
 */
export function useLivePlace(conversation: string | null) {
  const directory = useWorkspaceDirectory((state) => state);
  const threads = useDesktopShellStore((state) => state.syncedThreads);
  return useMemo(() => {
    const place = livePlace(directory, conversation);
    return {
      place,
      assistant: placeAssistant(place),
      privacy: livePrivacy(),
      onScreen: onScreenLabel(conversation, threads),
    };
  }, [conversation, directory, threads]);
}
